/**
 * Ontology Browser - Hierarchy Tree & Term Explorer
 *
 * Features:
 * - Hierarchy tree view (expandable nodes)
 * - Term search with autocomplete
 * - Details panel (properties, definition, relationships)
 * - Statistics dashboard
 * - Export button (Turtle/OWL/JSON-LD)
 *
 * @version 1.0.0
 * @date 2025-11-28
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import '../../styles/ontology-management.css';
import { OntologyGraphView } from './OntologyGraphView';
import GraphTraversalPanel from '../station-results/GraphTraversalPanel';
import OntologyImprovementsPanel from './OntologyImprovementsPanel';
import { KnowledgeArchitecturePanel } from './knowledge-architecture/KnowledgeArchitecturePanel';

// =============================================================================
// TYPES
// =============================================================================

interface OntologyBrowserProps {
  ontologyId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface OntologyDetails {
  ontology_id: string;
  name: string;
  namespace: string;
  version: string;
  format: string;
  description?: string;
  status: string;
  statistics: OntologyStatistics;
  parsed_content: ParsedContent;
  term_index: TermIndexEntry[];
  property_index: PropertyIndexEntry[];
  created_at: string;
  deployed_at?: string;
}

interface OntologyStatistics {
  term_count: number;
  class_count: number;
  property_count: number;
  individual_count: number;
  hierarchy_depth: number;
  max_breadth: number;
  triple_count: number;
  namespace_count: number;
}

interface ParsedContent {
  prefixes: Record<string, string>;
  classes: OntologyClass[];
  properties: OntologyProperty[];
  individuals: OntologyIndividual[];
  raw_triples_count: number;
}

interface OntologyClass {
  uri: string;
  local_name: string;
  label?: string;
  comment?: string;
  parent_classes?: string[];
  sub_classes?: string[];
}

interface OntologyProperty {
  uri: string;
  local_name: string;
  label?: string;
  comment?: string;
  type: 'object' | 'datatype' | 'annotation';
  domain?: string[];
  range?: string[];
  pattern?: string;
}

interface OntologyIndividual {
  uri: string;
  local_name: string;
  label?: string;
  types: string[];
  properties: Record<string, string[]>;
}

interface TermIndexEntry {
  id: string;
  uri: string;
  label: string;
  definition?: string;
  type: 'class' | 'property' | 'individual';
  parent_terms?: string[];
  child_terms?: string[];
  synonyms?: string[];
  deprecated?: boolean;
}

interface PropertyIndexEntry {
  id: string;
  uri: string;
  label: string;
  type: 'object' | 'datatype' | 'annotation';
  domain_labels?: string[];
  range_labels?: string[];
  pattern?: string;
}

interface TreeNode {
  id: string;
  label: string;
  uri: string;
  type: 'class' | 'property' | 'individual';
  children: TreeNode[];
  isExpanded?: boolean;
  depth: number;
}

type TabType = 'tree' | 'graph' | 'traversal' | 'classes' | 'properties' | 'individuals' | 'improvements' | 'architecture';
type ExportFormat = 'turtle' | 'owl' | 'jsonld';

// =============================================================================
// TREE NODE COMPONENT
// =============================================================================

interface TreeNodeProps {
  node: TreeNode;
  selectedId: string | null;
  onSelect: (node: TreeNode) => void;
  onToggle: (nodeId: string) => void;
  expandedNodes: Set<string>;
}

const TreeNodeComponent: React.FC<TreeNodeProps> = ({
  node,
  selectedId,
  onSelect,
  onToggle,
  expandedNodes,
}) => {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedId === node.id;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  const handleSelect = () => {
    onSelect(node);
  };

  const getTypeIcon = () => {
    switch (node.type) {
      case 'class':
        return 'C';
      case 'property':
        return 'P';
      case 'individual':
        return 'I';
      default:
        return '•';
    }
  };

  return (
    <div className="tree-node-container">
      <div
        className={`tree-node ${isSelected ? 'tree-node-selected' : ''}`}
        onClick={handleSelect}
        style={{ paddingLeft: `${node.depth * 20 + 8}px` }}
      >
        {hasChildren ? (
          <button
            className="tree-toggle"
            onClick={handleToggle}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="tree-toggle tree-toggle-spacer" />
        )}
        <span className={`tree-type-badge tree-type-${node.type}`}>
          {getTypeIcon()}
        </span>
        <span className="tree-label">{node.label}</span>
        {hasChildren && (
          <span className="tree-count">({node.children.length})</span>
        )}
      </div>

      {isExpanded && hasChildren && (
        <div className="tree-children">
          {node.children.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SEARCH RESULTS COMPONENT
// =============================================================================

interface SearchResultsProps {
  results: TermIndexEntry[];
  onSelect: (term: TermIndexEntry) => void;
  loading: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="search-results-loading">
        <div className="spinner-small" />
        Searching...
      </div>
    );
  }

  if (results.length === 0) {
    return <div className="search-results-empty">No results found</div>;
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'class':
        return 'C';
      case 'property':
        return 'P';
      case 'individual':
        return 'I';
      default:
        return '•';
    }
  };

  return (
    <div className="search-results">
      {results.map((term) => (
        <div
          key={term.id}
          className="search-result-item"
          onClick={() => onSelect(term)}
        >
          <span className={`tree-type-badge tree-type-${term.type}`}>
            {getTypeIcon(term.type)}
          </span>
          <div className="search-result-content">
            <span className="search-result-label">{term.label}</span>
            {term.definition && (
              <span className="search-result-definition">{term.definition}</span>
            )}
          </div>
          {term.deprecated && <span className="deprecated-badge">Deprecated</span>}
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// DETAILS PANEL COMPONENT
// =============================================================================

interface DetailsPanelProps {
  term: TermIndexEntry | null;
  ontology: OntologyDetails | null;
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({ term, ontology }) => {
  if (!term) {
    return (
      <div className="details-panel details-panel-empty">
        <div className="details-empty-icon">📋</div>
        <p>Select a term to view details</p>
      </div>
    );
  }

  // Find full class/property/individual details
  const findClassDetails = (): OntologyClass | null => {
    if (!ontology || term.type !== 'class') return null;
    return (
      ontology.parsed_content.classes.find((c) => c.uri === term.uri) || null
    );
  };

  const findPropertyDetails = (): OntologyProperty | null => {
    if (!ontology || term.type !== 'property') return null;
    return (
      ontology.parsed_content.properties.find((p) => p.uri === term.uri) || null
    );
  };

  const findIndividualDetails = (): OntologyIndividual | null => {
    if (!ontology || term.type !== 'individual') return null;
    return (
      ontology.parsed_content.individuals.find((i) => i.uri === term.uri) || null
    );
  };

  const classDetails = findClassDetails();
  const propertyDetails = findPropertyDetails();
  const individualDetails = findIndividualDetails();

  return (
    <div className="details-panel">
      <div className="details-header">
        <span className={`details-type-badge details-type-${term.type}`}>
          {term.type.toUpperCase()}
        </span>
        <h3 className="details-title">{term.label}</h3>
        {term.deprecated && <span className="deprecated-badge">Deprecated</span>}
      </div>

      <div className="details-section">
        <h4>URI</h4>
        <code className="details-uri">{term.uri}</code>
      </div>

      {term.definition && (
        <div className="details-section">
          <h4>Definition</h4>
          <p className="details-definition">{term.definition}</p>
        </div>
      )}

      {term.synonyms && term.synonyms.length > 0 && (
        <div className="details-section">
          <h4>Synonyms</h4>
          <div className="details-tags">
            {term.synonyms.map((syn, idx) => (
              <span key={idx} className="details-tag">
                {syn}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Class-specific details */}
      {classDetails && (
        <>
          {classDetails.parent_classes && classDetails.parent_classes.length > 0 && (
            <div className="details-section">
              <h4>Parent Classes</h4>
              <ul className="details-list">
                {classDetails.parent_classes.map((parent, idx) => (
                  <li key={idx}>{parent}</li>
                ))}
              </ul>
            </div>
          )}
          {classDetails.sub_classes && classDetails.sub_classes.length > 0 && (
            <div className="details-section">
              <h4>Subclasses</h4>
              <ul className="details-list">
                {classDetails.sub_classes.map((sub, idx) => (
                  <li key={idx}>{sub}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Property-specific details */}
      {propertyDetails && (
        <>
          <div className="details-section">
            <h4>Property Type</h4>
            <span className="property-type-badge">{propertyDetails.type}</span>
          </div>
          {propertyDetails.domain && propertyDetails.domain.length > 0 && (
            <div className="details-section">
              <h4>Domain</h4>
              <ul className="details-list">
                {propertyDetails.domain.map((d, idx) => (
                  <li key={idx}>{d}</li>
                ))}
              </ul>
            </div>
          )}
          {propertyDetails.range && propertyDetails.range.length > 0 && (
            <div className="details-section">
              <h4>Range</h4>
              <ul className="details-list">
                {propertyDetails.range.map((r, idx) => (
                  <li key={idx}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {propertyDetails.pattern && (
            <div className="details-section">
              <h4>Validation Pattern</h4>
              <code className="details-pattern">{propertyDetails.pattern}</code>
            </div>
          )}
        </>
      )}

      {/* Individual-specific details */}
      {individualDetails && (
        <>
          {individualDetails.types.length > 0 && (
            <div className="details-section">
              <h4>Types</h4>
              <div className="details-tags">
                {individualDetails.types.map((type, idx) => (
                  <span key={idx} className="details-tag">
                    {type}
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(individualDetails.properties).length > 0 && (
            <div className="details-section">
              <h4>Properties</h4>
              <table className="details-properties-table">
                <tbody>
                  {Object.entries(individualDetails.properties).map(([key, values]) => (
                    <tr key={key}>
                      <td className="property-key">{key}</td>
                      <td className="property-value">{values.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// =============================================================================
// STATISTICS DASHBOARD COMPONENT
// =============================================================================

interface StatsDashboardProps {
  statistics: OntologyStatistics | null;
}

const StatsDashboard: React.FC<StatsDashboardProps> = ({ statistics }) => {
  if (!statistics) {
    return <div className="stats-dashboard stats-loading">Loading statistics...</div>;
  }

  return (
    <div className="stats-dashboard">
      <div className="stats-row">
        <div className="stats-card">
          <span className="stats-icon">📊</span>
          <div className="stats-content">
            <span className="stats-value">{statistics.term_count.toLocaleString()}</span>
            <span className="stats-label">Total Terms</span>
          </div>
        </div>
        <div className="stats-card">
          <span className="stats-icon">🔷</span>
          <div className="stats-content">
            <span className="stats-value">{statistics.class_count.toLocaleString()}</span>
            <span className="stats-label">Classes</span>
          </div>
        </div>
        <div className="stats-card">
          <span className="stats-icon">🔗</span>
          <div className="stats-content">
            <span className="stats-value">{statistics.property_count.toLocaleString()}</span>
            <span className="stats-label">Properties</span>
          </div>
        </div>
        <div className="stats-card">
          <span className="stats-icon">📌</span>
          <div className="stats-content">
            <span className="stats-value">{statistics.individual_count.toLocaleString()}</span>
            <span className="stats-label">Individuals</span>
          </div>
        </div>
      </div>
      <div className="stats-row">
        <div className="stats-card stats-card-small">
          <div className="stats-content">
            <span className="stats-value">{statistics.hierarchy_depth}</span>
            <span className="stats-label">Hierarchy Depth</span>
          </div>
        </div>
        <div className="stats-card stats-card-small">
          <div className="stats-content">
            <span className="stats-value">{statistics.max_breadth}</span>
            <span className="stats-label">Max Breadth</span>
          </div>
        </div>
        <div className="stats-card stats-card-small">
          <div className="stats-content">
            <span className="stats-value">{statistics.triple_count.toLocaleString()}</span>
            <span className="stats-label">Triples</span>
          </div>
        </div>
        <div className="stats-card stats-card-small">
          <div className="stats-content">
            <span className="stats-value">{statistics.namespace_count}</span>
            <span className="stats-label">Namespaces</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const OntologyBrowser: React.FC<OntologyBrowserProps> = ({
  ontologyId,
  isOpen,
  onClose,
}) => {
  const { authFetch } = useApiClient();
  // State
  const [ontology, setOntology] = useState<OntologyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('tree');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TermIndexEntry[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<TermIndexEntry | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Fetch ontology details
  useEffect(() => {
    if (!isOpen || !ontologyId) return;

    const fetchOntology = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authFetch(`/api/ontologies/${ontologyId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch ontology: ${response.statusText}`);
        }

        const data: OntologyDetails = await response.json();
        setOntology(data);
      } catch (err: unknown) {
        console.error('[OntologyBrowser] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchOntology();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [isOpen, ontologyId]);

  // Build hierarchy tree
  const treeData = useMemo((): TreeNode[] => {
    if (!ontology?.parsed_content) return [];

    const classes = ontology.parsed_content.classes;
    const nodeMap = new Map<string, TreeNode>();

    // Create nodes for all classes
    classes.forEach((cls) => {
      nodeMap.set(cls.uri, {
        id: cls.uri,
        label: cls.label || cls.local_name,
        uri: cls.uri,
        type: 'class',
        children: [],
        depth: 0,
      });
    });

    // Build hierarchy
    const roots: TreeNode[] = [];
    classes.forEach((cls) => {
      const node = nodeMap.get(cls.uri);
      if (!node) return;

      if (cls.parent_classes && cls.parent_classes.length > 0) {
        // Add as child to first parent
        const parentUri = cls.parent_classes[0];
        const parent = nodeMap.get(parentUri);
        if (parent) {
          node.depth = parent.depth + 1;
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    // Sort children alphabetically
    const sortChildren = (nodes: TreeNode[]) => {
      nodes.sort((a, b) => a.label.localeCompare(b.label));
      nodes.forEach((n) => sortChildren(n.children));
    };
    sortChildren(roots);

    return roots;
  }, [ontology]);

  // Search terms
  const handleSearch = useCallback(
    async (query: string) => {
      if (!query.trim() || !ontologyId) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const response = await authFetch(
          `/api/ontologies/${ontologyId}/terms/search?q=${encodeURIComponent(query)}&limit=20`
        );

        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.results);
        }
      } catch (err) {
        console.error('[OntologyBrowser] Search error:', err);
      } finally {
        setSearching(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
    [ontologyId]
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Handle node toggle
  const handleNodeToggle = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Handle node selection
  const handleNodeSelect = (node: TreeNode) => {
    const term = ontology?.term_index.find((t) => t.uri === node.uri);
    if (term) {
      setSelectedTerm(term);
    }
  };

  // Handle search result selection
  const handleSearchSelect = (term: TermIndexEntry) => {
    setSelectedTerm(term);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle export
  const handleExport = (format: ExportFormat) => {
    console.log('[OntologyBrowser] Export:', format);
    alert(`Export to ${format.toUpperCase()} format coming soon!`);
  };

  if (!isOpen) return null;

  return (
    <div className="browser-overlay">
      <div className="browser-modal">
        {/* Header */}
        <div className="browser-header">
          <div className="browser-title-section">
            <h1>{ontology?.name || 'Loading...'}</h1>
            {ontology && (
              <span className="browser-namespace">
                {ontology.namespace}:{ontology.version}
              </span>
            )}
          </div>
          <div className="browser-actions">
            <div className="export-dropdown">
              <button className="btn btn-secondary">Export ▼</button>
              <div className="export-menu">
                <button onClick={() => handleExport('turtle')}>Turtle (.ttl)</button>
                <button onClick={() => handleExport('owl')}>OWL (.owl)</button>
                <button onClick={() => handleExport('jsonld')}>JSON-LD (.jsonld)</button>
              </div>
            </div>
            <button className="browser-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="browser-loading">
            <div className="spinner" />
            <p>Loading ontology...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="browser-error">
            <div className="error-icon">⚠️</div>
            <h3>Failed to Load Ontology</h3>
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && ontology && (
          <>
            {/* Statistics Dashboard */}
            <StatsDashboard statistics={ontology.statistics} />

            {/* Content Tabs */}
            <div className="browser-tabs">
              <button
                className={`browser-tab ${activeTab === 'tree' ? 'active' : ''}`}
                onClick={() => setActiveTab('tree')}
              >
                Hierarchy Tree
              </button>
              <button
                className={`browser-tab ${activeTab === 'graph' ? 'active' : ''}`}
                onClick={() => setActiveTab('graph')}
              >
                Graph View
              </button>
              <button
                className={`browser-tab ${activeTab === 'traversal' ? 'active' : ''}`}
                onClick={() => setActiveTab('traversal')}
              >
                Graph Traversal
              </button>
              <button
                className={`browser-tab ${activeTab === 'classes' ? 'active' : ''}`}
                onClick={() => setActiveTab('classes')}
              >
                Classes ({ontology.statistics.class_count})
              </button>
              <button
                className={`browser-tab ${activeTab === 'properties' ? 'active' : ''}`}
                onClick={() => setActiveTab('properties')}
              >
                Properties ({ontology.statistics.property_count})
              </button>
              <button
                className={`browser-tab ${activeTab === 'individuals' ? 'active' : ''}`}
                onClick={() => setActiveTab('individuals')}
              >
                Individuals ({ontology.statistics.individual_count})
              </button>
              <button
                className={`browser-tab ${activeTab === 'improvements' ? 'active' : ''}`}
                onClick={() => setActiveTab('improvements')}
              >
                🔬 Improvements
              </button>
              <button
                className={`browser-tab ${activeTab === 'architecture' ? 'active' : ''}`}
                onClick={() => setActiveTab('architecture')}
              >
                Knowledge Architecture
              </button>
            </div>

            {/* Content Area */}
            {/* Graph View - Full Width */}
            {activeTab === 'graph' && (
              <div className="browser-graph-fullwidth">
                <OntologyGraphView
                  parsedContent={ontology.parsed_content}
                  onNodeSelect={(uri) => {
                    const term = ontology.term_index.find((t) => t.uri === uri);
                    if (term) {
                      setSelectedTerm(term);
                      setActiveTab('tree'); // Switch to tree to see details
                    }
                  }}
                />
              </div>
            )}

            {/* Graph Traversal - Full Width with Scroll */}
            {activeTab === 'traversal' && (
              <div
                className="browser-graph-fullwidth"
                style={{
                  padding: '20px',
                  overflowY: 'auto',
                  display: 'block'
                }}
              >
                <GraphTraversalPanel ontologyId={ontologyId} />
              </div>
            )}

            {/* Improvements Tab - Full Width with Scroll */}
            {activeTab === 'improvements' && (
              <div
                className="browser-graph-fullwidth"
                style={{
                  padding: '20px',
                  overflowY: 'auto',
                  display: 'block'
                }}
              >
                <OntologyImprovementsPanel ontologyId={ontologyId} />
              </div>
            )}

            {/* Knowledge Architecture Tab - Full Width with Scroll */}
            {activeTab === 'architecture' && (
              <div
                className="browser-graph-fullwidth"
                style={{
                  padding: '20px',
                  overflowY: 'auto',
                  display: 'block'
                }}
              >
                <KnowledgeArchitecturePanel
                  ontologyId={ontologyId}
                  statistics={ontology.statistics}
                />
              </div>
            )}

            {/* Standard Content (Tree/List Views) */}
            {activeTab !== 'graph' && activeTab !== 'traversal' && activeTab !== 'improvements' && activeTab !== 'architecture' && (
              <div className="browser-content">
              {/* Left Panel: Search + Tree/List */}
              <div className="browser-left-panel">
                {/* Search */}
                <div className="browser-search">
                  <input
                    type="text"
                    placeholder="Search terms..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  {searchQuery && (
                    <SearchResults
                      results={searchResults}
                      onSelect={handleSearchSelect}
                      loading={searching}
                    />
                  )}
                </div>

                {/* Tree View */}
                {activeTab === 'tree' && (
                  <div className="browser-tree">
                    {treeData.length > 0 ? (
                      treeData.map((node) => (
                        <TreeNodeComponent
                          key={node.id}
                          node={node}
                          selectedId={selectedTerm?.uri || null}
                          onSelect={handleNodeSelect}
                          onToggle={handleNodeToggle}
                          expandedNodes={expandedNodes}
                        />
                      ))
                    ) : (
                      <div className="tree-empty">
                        No class hierarchy available
                      </div>
                    )}
                  </div>
                )}

                {/* Classes List */}
                {activeTab === 'classes' && (
                  <div className="browser-list">
                    {ontology.parsed_content.classes.map((cls) => (
                      <div
                        key={cls.uri}
                        className={`list-item ${
                          selectedTerm?.uri === cls.uri ? 'list-item-selected' : ''
                        }`}
                        onClick={() => {
                          const term = ontology.term_index.find((t) => t.uri === cls.uri);
                          if (term) setSelectedTerm(term);
                        }}
                      >
                        <span className="tree-type-badge tree-type-class">C</span>
                        <span className="list-label">{cls.label || cls.local_name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Properties List */}
                {activeTab === 'properties' && (
                  <div className="browser-list">
                    {ontology.parsed_content.properties.map((prop) => (
                      <div
                        key={prop.uri}
                        className={`list-item ${
                          selectedTerm?.uri === prop.uri ? 'list-item-selected' : ''
                        }`}
                        onClick={() => {
                          const term = ontology.term_index.find((t) => t.uri === prop.uri);
                          if (term) setSelectedTerm(term);
                        }}
                      >
                        <span className="tree-type-badge tree-type-property">P</span>
                        <span className="list-label">{prop.label || prop.local_name}</span>
                        <span className="property-type-small">{prop.type}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Individuals List */}
                {activeTab === 'individuals' && (
                  <div className="browser-list">
                    {ontology.parsed_content.individuals.map((ind) => (
                      <div
                        key={ind.uri}
                        className={`list-item ${
                          selectedTerm?.uri === ind.uri ? 'list-item-selected' : ''
                        }`}
                        onClick={() => {
                          const term = ontology.term_index.find((t) => t.uri === ind.uri);
                          if (term) setSelectedTerm(term);
                        }}
                      >
                        <span className="tree-type-badge tree-type-individual">I</span>
                        <span className="list-label">{ind.label || ind.local_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Panel: Details */}
              <div className="browser-right-panel">
                <DetailsPanel term={selectedTerm} ontology={ontology} />
              </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OntologyBrowser;
